import { useState } from 'react';
import type { EpisodeItem } from '@/types/content';

interface EpisodeSectionProps {
  dramaTitle: string;
  episodes: EpisodeItem[];
}

export default function EpisodeSection({ dramaTitle, episodes }: EpisodeSectionProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  const visible = showAll ? episodes : episodes.slice(0, 8);
  const current = episodes.find((ep) => ep.index === selected) ?? null;

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-center gap-2">
        <i className="ri-play-list-2-line text-lg text-primary-500" />
        <h2 className="font-heading text-lg font-bold text-foreground-950">分集列表</h2>
        <span className="rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-500">
          共 {episodes.length} 集
        </span>
      </div>
      <p className="mt-2 text-sm text-foreground-500">点击任意一集查看本集简要章节信息。</p>

      {current && (
        <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-heading text-base font-bold text-primary-800">
              第 {current.index} 集 · {current.title}
            </h3>
            <button
              type="button"
              onClick={() => setSelected(null)}
              aria-label="关闭本集信息"
              className="flex h-7 w-7 items-center justify-center rounded-full text-primary-600 transition-colors hover:bg-primary-100"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            《{dramaTitle}》第 {current.index} 集《{current.title}》，播出时间 {current.date}。
            本集围绕主线剧情持续推进，人物关系出现新的转折，为后续情节埋下重要伏笔。
          </p>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {visible.map((ep) => {
          const active = selected === ep.index;
          return (
            <button
              key={ep.index}
              type="button"
              onClick={() => setSelected(active ? null : ep.index)}
              className={`flex flex-col items-start rounded-md border px-3 py-2.5 text-left transition-colors ${
                active
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-background-200 bg-background-50 hover:border-primary-300'
              }`}
            >
              <span className={`text-sm font-label ${active ? 'text-primary-700' : 'text-foreground-800'}`}>
                第 {ep.index} 集
              </span>
              <span className="mt-0.5 line-clamp-1 w-full text-xs text-foreground-500">{ep.title}</span>
              <span className="mt-1 flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-calendar-line" />
                {ep.date}
              </span>
            </button>
          );
        })}
      </div>

      {episodes.length > 8 && (
        <button
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          className="mt-4 flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm font-label text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500"
        >
          {showAll ? '收起分集列表' : `展开全部 ${episodes.length} 集`}
          <i className={showAll ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        </button>
      )}
    </section>
  );
}